// Audio subsystem (Howler). Ports audio.c: WAV SFX + circuit hum loops and
// the 13 MP3 music tracks with the exact BackgroundMusic()/CircuitHum() logic.
// Heavy MP3 files may be absent; TRACK_FALLBACK maps them to lighter tracks.

import { Howl, Howler } from 'howler';
import { G, rooms } from '../core/state';
import { isAmbientMusicEnabled, isBattleMusicEnabled } from '../core/settings';

const BASE = './assets/';
function url(datPath: string): string {
  return BASE + datPath.replace(/^dat\//, '');
}

// Track table (audio.c tracks[13]), remapped to the converted MP3 files.
const TRACKS = [
  'dat/m/ICEFRONT.mp3',
  'dat/m/cavern.mp3',
  'dat/m/cave.mp3',
  'dat/m/cave06.mp3',
  'dat/m/Wood.mp3',
  'dat/m/iller_knarkloader_final.mp3',
  'dat/m/fear2.mp3',
  'dat/m/Cv_boss.mp3',
  'dat/m/Fr_boss.mp3',
  'dat/m/CT_BOSS.mp3',
  'dat/m/rpg_bat1.mp3',
  'dat/m/amblight.mp3',
  'dat/m/FINALBAT.mp3',
];

// When the primary track file is removed, use a lighter substitute (same index keys).
const TRACK_FALLBACK: Record<number, string> = {
  2: 'dat/m/cave06.mp3', // cave.mp3 removed
  3: 'dat/m/cave06.mp3', // deep dungeon — cave.mp3 removed
  6: 'dat/m/cavern.mp3', // fear2.mp3 removed
  7: 'dat/m/rpg_bat1.mp3', // Cv_boss.mp3 removed
  8: 'dat/m/rpg_bat1.mp3', // Fr_boss.mp3 removed
  9: 'dat/m/FINALBAT.mp3', // CT_BOSS.mp3 removed
};

// Preload immediately at boot (combat-critical + UI feedback).
const SFX_CRITICAL = [
  'dat/a/circuitrelease.wav',
  'dat/a/crystal.wav',
  'dat/a/crystal2.wav',
  'dat/a/enemyhit.wav',
  'dat/a/playerhurt.wav',
  'dat/a/shieldhit.wav',
  'dat/a/tone.wav',
];

// Lazy-loaded on first combat SFX (mons*shot).
const SFX_COMBAT = [
  'dat/a/mons0shot.wav',
  'dat/a/mons1shot.wav',
  'dat/a/mons2shot.wav',
  'dat/a/mons3shot.wav',
  'dat/a/mons4shot.wav',
  'dat/a/mons5shot.wav',
  'dat/a/mons6shot.wav',
  'dat/a/mons7shot.wav',
  'dat/a/mons8shot.wav',
  'dat/a/mons9shot.wav',
  'dat/a/mons10shot.wav',
];

const sfx: Record<string, Howl> = {};
let combatSfxLoaded = false;
let audioEnabled = false;

let musicHowl: Howl | null = null;
let musicVolume = 96 / 128;
let bgm_track = -1;
const musicCache: (Howl | null)[] = new Array(TRACKS.length).fill(null);

// Circuit hum: two looped samples on a dedicated channel (not in sfx map).
let humCharge: Howl | null = null;
let humRecover: Howl | null = null;
let humPlay = 0; // 0 none, 1 charge, 2 recover
let humId = 0;

function loadSfx(path: string): Howl {
  if (!sfx[path]) {
    sfx[path] = new Howl({ src: [url(path)], preload: true, html5: false, volume: 1 });
  }
  return sfx[path];
}

function loadCombatSfx(): void {
  if (combatSfxLoaded) return;
  combatSfxLoaded = true;
  for (const f of SFX_COMBAT) loadSfx(f);
}

function resolveTrack(idx: number): string {
  return TRACK_FALLBACK[idx] ?? TRACKS[idx];
}

function getMusicHowl(idx: number): Howl {
  const src = resolveTrack(idx);
  const cached = musicCache[idx];
  if (cached) return cached;
  const h = new Howl({ src: [url(src)], loop: true, html5: true, volume: 0 });
  musicCache[idx] = h;
  return h;
}

export function initAudio(): void {
  for (const f of SFX_CRITICAL) loadSfx(f);
  humCharge = new Howl({ src: [url('dat/a/circuitcharge.wav')], loop: true, volume: 0 });
  humRecover = new Howl({ src: [url('dat/a/circuitrecover.wav')], loop: true, volume: 0 });
}

export function enableAudio(): void {
  if (audioEnabled) return;
  audioEnabled = true;
  const ctx = (Howler as any).ctx as AudioContext | undefined;
  if (ctx && ctx.state === 'suspended') void ctx.resume();
}

export function isAudioEnabled(): boolean {
  return audioEnabled;
}

function isBattleTrack(idx: number): boolean {
  return idx === 7 || idx === 8 || idx === 9 || idx === 12;
}

function shouldPlayMusic(idx: number): boolean {
  if (idx === -1) return true;
  if (isBattleTrack(idx)) return isBattleMusicEnabled();
  return isAmbientMusicEnabled();
}

export function onMusicSettingChanged(): void {
  if (bgm_track === -1) return;
  if (!shouldPlayMusic(bgm_track)) {
    if (musicHowl) musicHowl.stop();
    return;
  }
  if (audioEnabled && musicHowl && !musicHowl.playing()) musicHowl.play();
}

export function isBgmMuted(): boolean {
  return !isAmbientMusicEnabled() && !isBattleMusicEnabled();
}

export function setBgmMuted(muted: boolean): void {
  // Legacy alias: mute/unmute both music categories.
  if (muted) {
    if (musicHowl) musicHowl.stop();
  }
}

function clampVol(vol: number): number {
  let v = vol / 128;
  if (v < 0) v = 0;
  if (v > 1) v = 1;
  return v;
}

export function SND_Play(filename: string, vol: number): void {
  if (!audioEnabled) return;
  if (vol <= 0) return;
  if (filename.includes('mons') && filename.endsWith('shot.wav')) loadCombatSfx();
  const h = sfx[filename];
  if (!h) return;
  const id = h.play();
  h.volume(clampVol(vol), id);
}

export function SND_Pos(filename: string, vol: number, dist: number): void {
  if (dist > 1600) return;
  const real_vol = (vol * (40 - Math.sqrt(dist))) / 40;
  SND_Play(filename, real_vol);
}

export function SND_CircuitRelease(str: number): void {
  SND_Play(
    'dat/a/circuitrelease.wav',
    Math.sqrt(str * 2 + (str * 5300) / G.circuit_size + (str > 100 ? 5300 : str * 53))
  );
}

export function MusicUpdate(): void {
  BackgroundMusic();
  CircuitHum();
}

function CircuitHum(): void {
  if (!audioEnabled || !humCharge || !humRecover) return;
  let hum_vol = 0;

  if (G.magic_circuit === 0) {
    hum_vol = 0;
    if (humPlay !== 0) {
      if (humPlay === 1) humCharge.stop(humId);
      else humRecover.stop(humId);
      humPlay = 0;
    }
  }
  if (G.magic_circuit > 0) {
    hum_vol = Math.trunc((G.circuit_size * 24) / 3000) + 12;
    hum_vol = Math.trunc((Math.trunc((G.magic_circuit * 96) / G.circuit_size) * hum_vol) / 36) + 32;
    if (humPlay !== 1) {
      if (humPlay === 2) humRecover.stop(humId);
      humId = humCharge.play();
      humPlay = 1;
    }
    humCharge.volume(clampVol(hum_vol), humId);
  }
  if (G.magic_circuit < 0) {
    hum_vol = Math.trunc(((G.circuit_size - (G.magic_circuit + G.circuit_size)) * 80) / 3000);
    if (humPlay !== 2) {
      if (humPlay === 1) humCharge.stop(humId);
      humId = humRecover.play();
      humPlay = 2;
    }
    humRecover.volume(clampVol(hum_vol), humId);
  }
}

function playTrack(idx: number, vol: number): void {
  if (musicHowl) musicHowl.stop();

  if (idx === -1) {
    musicHowl = null;
    bgm_track = idx;
    return;
  }

  bgm_track = idx;

  if (!shouldPlayMusic(idx)) {
    musicHowl = getMusicHowl(idx);
    return;
  }

  const next = getMusicHowl(idx);
  next.volume(vol);
  if (audioEnabled) next.play();
  musicHowl = next;
}

export function TitleScreenMusic(): void {
  playTrack(5, musicVolume);
}

export function ensureMusicPlaying(): void {
  if (!audioEnabled || bgm_track === -1) return;
  if (!shouldPlayMusic(bgm_track)) return;
  if (musicHowl && !musicHowl.playing()) musicHowl.play();
}

function BackgroundMusic(): void {
  let new_track = -1;
  musicVolume = 96 / 128;

  const sd = rooms[G.player_room].s_dist;
  if (sd <= 15) new_track = 0;

  if (bgm_track === 1) {
    if (sd <= 30 && sd >= 10) new_track = 1;
  }
  if (bgm_track === 2) {
    if (sd <= 45 && sd >= 25) new_track = 2;
  }

  if (new_track === -1) {
    if (sd <= 30 && sd >= 16) new_track = 1;
    if (sd <= 39 && sd >= 31) new_track = 2;
    if (sd >= 40) new_track = 3;
  }

  if (rooms[G.player_room].room_type === 3) new_track = 4;
  if (G.artifacts[11]) new_track = 6;

  if (rooms[G.player_room].room_type === 2) {
    if (G.boss_fight_mode === 2) {
      if (G.current_boss === 3 && G.player_shield === 30) {
        new_track = G.boss_lives <= 1 ? 7 : 12;
      } else {
        new_track = 7 + G.current_boss;
      }
      musicVolume = 128 / 128;
    } else {
      new_track = -1;
    }
  }

  if (G.player_room === 0 && G.current_boss === 3 && G.boss_fight_mode >= 3) {
    new_track = 11;
  }

  if (musicHowl) musicHowl.volume(musicVolume);
  if (new_track === bgm_track) return;
  playTrack(new_track, musicVolume);
}
